import type { Actuator, ActuatorUUID } from "../../types/scene";
import { create } from "mutative";
import { generateUuid } from "../../utils/id";
import { withHistory } from "./withHistory";
import type { SceneStoreSetState, SceneStoreGetState } from "../sceneStore.types";
import type { ActuatorActions, ActuatorInput } from "./actionTypes";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { namesFromRecord, nextUniqueName } from "../../utils/uniqueNames";

export const ensureActuatorUuid = (actuator: Partial<Actuator> | Actuator): Actuator => {
  if (actuator.uuid) return actuator as Actuator;
  return { ...actuator, uuid: generateUuid() } as Actuator;
};

export const normalizeActuatorRecord = (
  map: Record<string, Actuator | Partial<Actuator>> | undefined
): Record<ActuatorUUID, Actuator> => {
  const byId: Record<ActuatorUUID, Actuator> = {};
  Object.entries(map || {}).forEach(([key, actuator]) => {
    if (!actuator) return;
    const withName = actuator.name ? actuator : { ...actuator, name: key };
    const withUuid = ensureActuatorUuid(withName);
    byId[withUuid.uuid] = withUuid;
  });
  return byId;
};

export const createActuatorActions = (
  set: SceneStoreSetState,
  _get: SceneStoreGetState
): ActuatorActions => ({
  setActuators: (actuators: Record<string, Actuator | Partial<Actuator>>) => {
    if (shouldBlockSceneAuthoringMutation("setActuators")) return;
    set((state) =>
      create(state, (draft) => {
        draft.actuators = normalizeActuatorRecord(actuators);
      })
    );
  },

  addActuator: withHistory((actuator: ActuatorInput): ActuatorUUID => {
    const assignedUuid = (actuator.uuid ?? generateUuid()) as ActuatorUUID;
    set((state) =>
      create(state, (draft) => {
        if (!draft.actuators) {
          draft.actuators = {};
        }
        const finalActuator = ensureActuatorUuid({
          ...actuator,
          uuid: assignedUuid,
          name: nextUniqueName(actuator.name || "actuator", namesFromRecord(draft.actuators)),
        });
        draft.actuators[assignedUuid] = finalActuator;
      })
    );
    return assignedUuid;
  }),

  updateActuator: withHistory((uuid: ActuatorUUID, patch: Partial<Actuator>) => {
    set((state) =>
      create(state, (draft) => {
        if (!draft.actuators?.[uuid]) return;
        draft.actuators[uuid] = { ...draft.actuators[uuid], ...patch };
      })
    );
  }),

  removeActuator: withHistory((uuid: ActuatorUUID) => {
    set((state) =>
      create(state, (draft) => {
        delete draft.actuators?.[uuid];
      })
    );
  }),
});
